// Field help — bottom-sheet host (v1's only tenant-reachable variant, design
// D2). Chrome mirrors `PickerSheet`: backdrop, grab handle, sticky header,
// dialog semantics with focus trap and focus-restore, and its own layer in the
// existing Esc stack — mounted only while open, so it registers above
// whatever was already open (the overlay, always; a picker or the discard
// confirm, if also open) and Esc closes it first (`useFocusTrap`/`useEscLayer`,
// task 5.1's existing hooks — not reimplemented here).

import { useEffect, useRef } from 'react';
import styles from './ConversationalForms.module.css';
import FieldHelpThread, { HelpMessage } from './FieldHelpThread';
import { scheduleHelpFocusFallback } from './helpFocusFallback';
import { CloseIcon, SparklesIcon } from './icons';
import { CfField } from './types';
import { useEscLayer, useFocusTrap } from './useFocusTrap';

type FieldHelpSheetProps = {
    field: CfField;
    sectionTitle: string;
    thread: HelpMessage[];
    busy: boolean;
    slow: boolean;
    onSend: (text: string) => boolean;
    onRetry: (index: number, text: string) => void;
    onClose: () => void;
    lockedMessage?: string;
};

const FieldHelpSheet = ({
    field,
    sectionTitle,
    thread,
    busy,
    slow,
    onSend,
    onRetry,
    onClose,
    lockedMessage,
}: FieldHelpSheetProps) => {
    // Topmost layer while open, exactly like the picker: Esc closes only this
    // sheet, and the trap hands focus back to the invoking Ask-AI control on
    // close (the control was `document.activeElement` when the sheet mounted).
    useEscLayer(onClose);
    const trapRef = useFocusTrap<HTMLDivElement>();

    // Declared AFTER the trap, exactly like `PickerSheet`'s own search-row
    // autofocus: the trap's `prev` (what to restore focus to) is captured by
    // ITS effect, and effects in one component fire in declaration order —
    // claiming the composer here, after the trap, means the trap has already
    // captured the invoking control before this steals focus into the sheet.
    const composerRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        composerRef.current?.focus();
    }, []);

    // Adversarial-review R4: the Close control the composer's own lock-eject
    // effect (inside `FieldHelpThread`) redirects focus to when the lock
    // engages while the composer has it.
    const closeButtonRef = useRef<HTMLButtonElement>(null);

    // Adversarial-review R3/M1/N2: the trap's own restore targets the Ask-AI
    // control (`prev`, captured on mount) — but a fill committed through THIS
    // thread can leave the row collapsed by the time the sheet closes (the
    // form-view contract requires a confirmed scalar to collapse once its
    // thread isn't open), or a conditional parent change can hide the row
    // entirely, either of which removes the trap's restore target from the
    // DOM before its cleanup runs. `scheduleHelpFocusFallback` falls back to
    // the row (focusable either collapsed or expanded — N2a) or, failing
    // that, the overlay's own anchor (N2b).
    useEffect(() => {
        return () => scheduleHelpFocusFallback(field.id);
    }, [field.id]);

    return (
        <div className={styles.sheetBackdrop} onClick={onClose} data-testid="cf-help-sheet">
            <div
                ref={trapRef}
                className={styles.sheet}
                role="dialog"
                aria-modal="true"
                aria-label={`Assistant — ${field.label}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className={styles.sheetHandle} aria-hidden="true" />
                <div className={styles.sheetHeader}>
                    <span className={styles.helpHeader}>
                        <SparklesIcon /> Assistant · {sectionTitle}
                    </span>
                    <button
                        ref={closeButtonRef}
                        type="button"
                        className={styles.sheetClose}
                        onClick={onClose}
                        aria-label="Close help"
                    >
                        <CloseIcon />
                    </button>
                </div>
                <FieldHelpThread
                    field={field}
                    thread={thread}
                    busy={busy}
                    slow={slow}
                    onSend={onSend}
                    onRetry={onRetry}
                    composerRef={composerRef}
                    closeButtonRef={closeButtonRef}
                    lockedMessage={lockedMessage}
                />
            </div>
        </div>
    );
};

export default FieldHelpSheet;
